import React, { Fragment } from "react"
import { ArrowLeft, Menu as Bars3Icon, X as XMarkIcon } from "lucide-react"
import { Badge } from "@malond/ui/components/badge"
import { getUserFullName } from "@malond/utils"
import { Menu, Popover, Transition } from "@headlessui/react"
import { DefaultAvatar } from "../../libs/vars/url"
import Image from "next/image"
import { useUser } from "../../hooks/useUser"
import { useAuth } from "../../hooks/useAuth"
import { useMainEnterpriseId } from "../../hooks/enterprise"
import { useShopId } from "../../hooks/shop"
import { useShopSalePointId } from "../../hooks/shopSalePoint"
import Link from "next/link"
import {
  checkoutDiscountAtom,
  checkoutIsNeedCalculateSumBackAtom,
  checkoutItemsAtom,
  checkoutModalDiscountOpenAtom,
  checkoutRaisePercentAtom,
  checkoutStepAtom,
  checkoutSumGiveAtom,
} from "../../store/checkout"
import { useAtom as GetAtom } from "jotai"
import classNames from "classnames"
import { useRouter } from "next/router"

export interface UserNavigationProps {
  name: string
  href: string
}

function NavSaleHeader({ className }: { className: string }) {
  const { user } = useUser()
  const { userName, userProfile, activeShop } = useAuth()
  const enterpriseId = useMainEnterpriseId()
  const shopId = useShopId()
  const shopSalePointId = useShopSalePointId()

  const userFullName = getUserFullName(user?.lastName, user?.firstName)
  const displayName =
    (userFullName !== "Не указано" ? userFullName : null) ||
    user?.username ||
    userProfile?.username ||
    userName ||
    "Кассир"

  const computedNavigation: Array<UserNavigationProps> = [
    ...(userProfile?.shops && userProfile.shops.length > 1
      ? [{ name: "Сменить магазин", href: "/select-shop" }]
      : []),
    { name: "Выйти", href: "/logout" },
  ]

  const router = useRouter()

  const [, setOpen] = GetAtom(checkoutModalDiscountOpenAtom)
  const [, setIsNeedCalculateSumBack] = GetAtom(
    checkoutIsNeedCalculateSumBackAtom
  )
  const [_, setCheckoutItems] = GetAtom(checkoutItemsAtom)
  const [, setCheckoutDiscount] = GetAtom(checkoutDiscountAtom)
  const [, setRaisePercent] = GetAtom(checkoutRaisePercentAtom)
  const [, setCheckoutStep] = GetAtom(checkoutStepAtom)
  const [, setCheckoutSumGive] = GetAtom(checkoutSumGiveAtom)

  const clearSale = async () => {
    setOpen(false)
    setIsNeedCalculateSumBack(false)
    setCheckoutItems([])
    setCheckoutDiscount("0")
    setRaisePercent("0")
    setCheckoutStep("0")
    setCheckoutSumGive("0")

    await router.push(`/e/${enterpriseId}/${shopId}/${shopSalePointId}/sales`)
  }

  return (
    <Popover as="header" className={className}>
      {({ open }) => (
        <>
          <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6 lg:max-w-7xl lg:px-8 lg:py-0">
            <div className="relative flex items-center justify-center py-5 lg:justify-between">
              {/* Logo */}
              <div className="absolute left-0 flex-shrink-0 lg:static">
                <Link
                  href={`/e/${enterpriseId}/${shopId}/${shopSalePointId}`}
                  className="inline-flex items-center rounded-md border border-input bg-card px-4 py-2 text-base font-medium text-foreground shadow-xs hover:bg-accent focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none"
                >
                  <ArrowLeft
                    className="h-5 w-5 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="ml-3">Назад</span>
                </Link>

                {/*<a href="#">*/}
                {/*  <span className="sr-only">{DefaultSiteName}</span>*/}
                {/*  <img*/}
                {/*    className="h-8 w-auto"*/}
                {/*    src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=300"*/}
                {/*    alt={DefaultSiteName}*/}
                {/*  />*/}
                {/*</a>*/}
              </div>

              {/* Navigation section on desktop */}
              <div className="hidden lg:-my-px lg:ml-6 lg:flex lg:flex-1 lg:items-center lg:space-x-4">
                {/* Navigation on Desktop */}
                <button
                  className={classNames(
                    "bg-opacity-0 hover:bg-opacity-10 rounded-md bg-white px-3 py-2 text-sm font-medium",
                    {
                      "text-white": true,
                      "text-indigo-100": false,
                    }
                  )}
                  onClick={() => clearSale()}
                >
                  Новая продажа
                </button>

                {activeShop?.title && (
                  <Badge
                    variant="secondary"
                    className="border border-border px-2.5 py-1 text-xs font-medium"
                  >
                    {activeShop.title}
                  </Badge>
                )}
              </div>

              {/* Right section on desktop */}
              <div className="hidden lg:ml-4 lg:flex lg:items-center lg:pr-0.5">
                {/* Profile dropdown */}
                <Menu as="div" className="relative ml-4 flex-shrink-0">
                  <div>
                    <Menu.Button className="ring-opacity-20 focus:ring-opacity-100 flex items-center space-x-2 rounded-full bg-white px-2.5 py-1 text-sm ring-2 ring-white hover:bg-gray-50 focus:outline-none">
                      <span className="sr-only">Open user menu</span>

                      <Image
                        src={DefaultAvatar}
                        alt={"User avatar"}
                        width={28}
                        height={28}
                        className="h-7 w-7 rounded-full"
                      />
                      <span className="hidden max-w-[120px] truncate text-xs font-medium text-gray-700 md:inline-block">
                        {displayName}
                      </span>
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="ring-opacity-5 absolute -right-2 z-10 mt-2 w-52 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-none">
                      <div className="border-b border-gray-100 px-4 py-2">
                        <p className="text-xs text-gray-500">Вошли как</p>
                        <p className="truncate text-sm font-medium text-gray-900">
                          {displayName}
                        </p>
                        {userProfile?.role && (
                          <span className="mt-0.5 inline-block text-[10px] font-semibold tracking-wider text-indigo-600 uppercase">
                            {userProfile.role}
                          </span>
                        )}
                      </div>
                      {computedNavigation.map((item) => (
                        <Menu.Item key={item.name}>
                          {({ active }) => (
                            <Link
                              href={item.href}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              {item.name}
                            </Link>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>

              {/* Menu button */}
              <div className="absolute right-0 flex-shrink-0 lg:hidden">
                {/* Mobile menu button */}
                <Popover.Button className="hover:bg-opacity-10 inline-flex items-center justify-center rounded-md bg-transparent p-2 text-indigo-200 hover:bg-white hover:text-white focus:ring-2 focus:ring-white focus:outline-none">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Popover.Button>
              </div>
            </div>
          </div>

          <Transition.Root as={Fragment}>
            <div className="lg:hidden">
              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Popover.Overlay className="bg-opacity-25 fixed inset-0 z-20 bg-black" />
              </Transition.Child>

              <Transition.Child
                as={Fragment}
                enter="duration-150 ease-out"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="duration-150 ease-in"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Popover.Panel
                  focus
                  className="absolute inset-x-0 top-0 z-30 mx-auto w-full max-w-3xl origin-top transform p-2 transition"
                >
                  <div className="ring-opacity-5 divide-y divide-gray-200 rounded-lg bg-white shadow-lg ring-1 ring-black">
                    <div className="pt-3 pb-2">
                      <div className="flex items-center justify-between px-4">
                        <Link
                          href={`/e/${enterpriseId}/${shopId}/${shopSalePointId}`}
                          className="inline-flex items-center rounded-md border border-input bg-card px-4 py-2 text-base font-medium text-foreground shadow-xs hover:bg-accent focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none"
                        >
                          <ArrowLeft
                            className="h-5 w-5 text-muted-foreground"
                            aria-hidden="true"
                          />
                          <span className="ml-3">Назад</span>
                        </Link>

                        {/*<div>*/}
                        {/*  <img*/}
                        {/*    className="h-8 w-auto"*/}
                        {/*    src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"*/}
                        {/*    alt={DefaultSiteName}*/}
                        {/*  />*/}
                        {/*</div>*/}
                        <div className="-mr-2">
                          <Popover.Button className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none focus:ring-inset">
                            <span className="sr-only">Close menu</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </Popover.Button>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1 px-2">
                        {/* Navigation on Mobile */}
                        <button
                          className={classNames(
                            "block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-800"
                          )}
                          onClick={() => clearSale()}
                        >
                          Новая продажа
                        </button>
                      </div>
                    </div>
                    <div className="pt-4 pb-2">
                      <div className="flex items-center px-5">
                        <div className="flex-shrink-0">
                          <Image
                            src={DefaultAvatar}
                            alt={"User avatar"}
                            width={32}
                            height={32}
                            className="h-10 w-10 rounded-full"
                          />
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="truncate text-base font-medium text-gray-800">
                            {displayName}
                          </div>
                          <div className="truncate text-sm font-medium text-gray-500">
                            {activeShop?.title ? `${activeShop.title} • ` : ""}
                            {user?.username || userProfile?.email || ""}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1 px-2">
                        {computedNavigation.map((item) => (
                          <Link
                            key={item.name}
                            href={item.href}
                            className="block rounded-md px-3 py-2 text-base font-medium text-gray-900 hover:bg-gray-100 hover:text-gray-800"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </Popover.Panel>
              </Transition.Child>
            </div>
          </Transition.Root>
        </>
      )}
    </Popover>
  )
}

export default NavSaleHeader
